import { useEffect, useState } from 'react'
import { Loader2, CheckCircle2, XCircle, ArrowRight } from 'lucide-react'
import { Navbar } from '../components/Navbar'
import { Footer } from '../components/Footer'
import { writeRoute } from '../App'

const VIEW_STORAGE_KEY = 'scoutang:view'
const VIEW_STORAGE_KEY_PERSISTENT = 'scoutang:view:persistent'

export default function PaystackCallback() {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [message, setMessage] = useState('Verifying your payment...')
  const [reference, setReference] = useState<string | null>(null)

  useEffect(() => {
    const url = new URL(window.location.href)
    const ref = url.searchParams.get('reference') || url.searchParams.get('trxref')
    if (ref) setReference(ref)
  }, [])

  useEffect(() => {
    async function verifyPayment() {
      if (!reference) {
        setStatus('error')
        setMessage('No payment reference found. Please contact support.')
        return
      }

      try {
        const { backendClient } = await import('../utils/backend')
        const { data: { session } } = await backendClient.auth.getSession()
        const accessToken = session?.access_token

        if (!accessToken) {
          setStatus('error')
          setMessage('Session expired. Please log in again.')
          return
        }

        const res = await fetch(`/api/paystack/verify-payment?reference=${encodeURIComponent(reference)}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        })

        const data = await res.json()

        if (res.ok && data.ok) {
          setStatus('success')
          setMessage('Payment verified successfully! Activating your subscription...')
          
          // Wait a moment then redirect to dashboard using custom routing
          setTimeout(() => {
            window.sessionStorage.setItem(VIEW_STORAGE_KEY, 'dashboard')
            window.localStorage.setItem(VIEW_STORAGE_KEY_PERSISTENT, 'dashboard')
            writeRoute('dashboard', true)
            window.location.reload()
          }, 2000)
        } else {
          setStatus('error')
          setMessage(data.error || 'Payment verification failed. Please contact support.')
        }
      } catch (err: any) {
        setStatus('error')
        setMessage(err.message || 'An error occurred. Please contact support.')
      }
    }

    if (reference) verifyPayment()
  }, [reference])

  const goToDashboard = () => {
    window.sessionStorage.setItem(VIEW_STORAGE_KEY, 'dashboard')
    window.localStorage.setItem(VIEW_STORAGE_KEY_PERSISTENT, 'dashboard')
    writeRoute('dashboard', true)
    window.location.reload()
  }

  const goToPricing = () => {
    window.sessionStorage.setItem(VIEW_STORAGE_KEY, 'pricing')
    window.localStorage.setItem(VIEW_STORAGE_KEY_PERSISTENT, 'pricing')
    writeRoute('pricing', true)
    window.location.reload()
  }

  const goToSupport = () => {
    window.sessionStorage.setItem(VIEW_STORAGE_KEY, 'support')
    window.localStorage.setItem(VIEW_STORAGE_KEY_PERSISTENT, 'support')
    writeRoute('support', true)
    window.location.reload()
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#050910] text-white">
      <Navbar currentView="home" onNavigate={() => {}} onLoginClick={() => {}} onLogoutClick={() => {}} user={null} />
      
      <main className="flex-1 flex items-center justify-center px-4 py-20">
        <div className="max-w-md w-full surface-panel-low rounded-3xl p-8 text-center border border-white/10 shadow-tactical">
          {status === 'loading' && (
            <>
              <div className="w-16 h-16 mx-auto mb-6">
                <Loader2 className="w-full h-full text-[#7cff01] animate-spin" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Verifying Payment</h2>
              <p className="text-slate-300">{message}</p>
              <p className="text-xs text-slate-500 mt-4">Please do not close this window</p>
            </>
          )}
          
          {status === 'success' && (
            <>
              <div className="w-16 h-16 mx-auto mb-6 bg-green-500/10 rounded-full flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-green-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Payment Successful!</h2>
              <p className="text-slate-300 mb-6">{message}</p>
              <button
                onClick={goToDashboard}
                className="btn-primary flex items-center justify-center gap-2 mx-auto"
              >
                Go to Dashboard <ArrowRight size={18} />
              </button>
            </>
          )}
          
          {status === 'error' && (
            <>
              <div className="w-16 h-16 mx-auto mb-6 bg-red-500/10 rounded-full flex items-center justify-center">
                <XCircle className="w-8 h-8 text-red-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Verification Failed</h2>
              <p className="text-slate-300 mb-6">{message}</p>
              <div className="flex flex-col gap-3">
                <button
                  onClick={goToPricing}
                  className="btn-secondary"
                >
                  Back to Pricing
                </button>
                <button
                  onClick={goToSupport}
                  className="btn-tertiary"
                >
                  Contact Support
                </button>
              </div>
            </>
          )}
        </div>
      </main>
      
      <Footer onNavigate={() => {}} />
    </div>
  )
}